import { Component, Input, Renderer2, ChangeDetectorRef } from '@angular/core';
import { aggregateBy } from '@progress/kendo-data-query';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { BaseGridEditFormLinesComponent } from '@app/cp/shared/base-components/base-grid-edit-formlines.component';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { BaseMessageInfo } from '@app/cp/shared/models/BaseMessageInfo';
import { ConstructionService } from '@app/cp/shared/services/construction.service';
import { CommonFunctions } from '@app/cp/shared/static/common-functions';
import { MSCLFilterClass } from '@app/cp/shared/classes/filters/MSCLFilterClass';
import { DispatchPMCompleteAdjustmentBus } from '@app/cp/shared/models/DispatchPMCompleteAdjustmentBus';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";

/*******************************************************************************************************
 * Name: DispatchCostAdjustmentsComponent
 * Creator: GRG
 * Date: 20210330
 * OverridesAndExtras:
 *      1) addModeOnly input - can be used so that grid does not show and only popup window shows
 *      2) dispatchId set input property - if set then windows shows and saves with that id
 *      3) after set edit - used because there is no editing of this so set readonly to show as view
 *      4) originalReadOnly - so can return readonly to original state if viewing from grid
 *      5) customCanSaveForm - if code is 'Other' then require note
 * Notes:
 * Modified:
 *******************************************************************************************************/

@Component({
  selector: 'cp-dispatch-complete-adjustment',
  templateUrl: './dispatch-complete-adjustment.component.html',
  styleUrls: ['./dispatch-complete-adjustment.component.scss']
})
export class DispatchCompleteAdjustmentComponent extends BaseGridEditFormLinesComponent<DispatchPMCompleteAdjustmentBus, null, ConstructionService> {

    costCodeCF: MSCLFilterClass;

    @Input() addModeOnly: boolean = false; //Do not show grid -
    @Input() multiAddMode: boolean = false; //Multiple Job Adjustment -
    private _dispatchId: any;
    @Input() set dispatchId(id: any) {
        if (CommonFunctions.hasGuidValue(id)) {
            this._parentId = id;
            this._dispatchId = id;
            this.addHandler();
        }
    }
    get dispatchId() {
        return this._dispatchId;
    }
    private _jobIds: any;
    @Input() set jobIds(ids: any[]) {
        this._jobIds = ids;
        if (this.multiAddMode && ids) {
            this.createDynamicFormFields();
        }
    }
    get jobIds() {
        return this._jobIds;
    }
    private _jobNbrs: any;
    @Input() set jobNbrs(ids: any[]) {
        this._jobNbrs = ids;
    }
    get jobNbrs() {
        return this._jobNbrs;
    }
    get noteCancelButtonText(): string {
        return this.readOnly ? "Ok" : "Cancel";
    }
    afterSetEdit() {
        this.originalReadOnly = this.readOnly;
        this.readOnly = !this.isNew;
    }
    protected closeEditor(): void {
        super.closeEditor();
        this.readOnly = this.originalReadOnly;
    }
    get dispatchCostAdjustmentCodeId(): any {
        return this.hasEditForm ? this.editForm.value.dispatchCostAdjustmentCodeId : null;
    }
    originalReadOnly: boolean;
    customCanSaveForm(): boolean {
        let canSave: boolean = true;
        //if (CommonFunctions.hasGuidValue(this.dispatchCostAdjustmentCodeId) && this.dispatchCostAdjustmentCodeId === CommonValues.DispatchCostAdjustmentCode_Other) {
        //    canSave = CommonFunctions.hasStringValue(this.editForm.value.note);
        //}
        return canSave;
    }

    constructor(globals: GlobalsService, renderer: Renderer2, cd: ChangeDetectorRef, svc_data: ConstructionService, featureFlagService: FeatureFlagService) {
        super(globals, renderer, cd, featureFlagService);
        this.svc_data = svc_data;
        this.className = "DispatchPMCompleteAdjustmentBus";
        this.appCode = "ContractPMCompleteAdjustments";
        this.appName = "Job PM Complete Adjustment(s)";
        this.aggregates = [
            { field: 'amount', aggregate: 'sum' }
        ];
        this.disableCpSearchDialog();
    }
    createForm() {
        this.editForm = new UntypedFormGroup({
            'dispatchPMCompleteAdjustmentId': new UntypedFormControl(),
            //'dispatchPMCompleteAdjustmentCodeId': new FormControl('', Validators.required),
            'dispatchId': new UntypedFormControl(),
            'personId': new UntypedFormControl(),
            'note': new UntypedFormControl('', Validators.required),
            'isComplete': new UntypedFormControl(),//'', Validators.required),
            'insertDt': new UntypedFormControl(),
            'insertUserId': new UntypedFormControl(),
            'updateDt': new UntypedFormControl(),
            'updateUserId': new UntypedFormControl()
        });
    }
    createDynamicFormFields() {
        if (this.jobIds && this.jobIds.length > 0) {
            this.jobIds.forEach((element, index) => {
                this.editForm.addControl('subCheck-' + index, new UntypedFormControl());
            });
        }
    }
    setForm() {
        this.editForm.patchValue({
            dispatchPMCompleteAdjustmentId: this.selectedData.dispatchPMCompleteAdjustmentId,
            //dispatchPMCompleteAdjustmentCodeId: this.selectedData.dispatchPMCompleteAdjustmentCodeId,
            dispatchId: this.selectedData.dispatchId,
            personId: this.selectedData.personId,
            note: this.selectedData.note,
            isComplete: this.selectedData.isComplete,
            insertDt: this.selectedData.insertDt,
            insertUserId: this.selectedData.insertUserId,
            updateDt: this.selectedData.updateDt,
            updateUserId: this.selectedData.updateUserId
        });
    }
    completeCheckBoxChanged(e: any) {
        this.editForm.patchValue({
            isComplete: e.target.checked
        });
    }
    protected getChangedObject(dataObj: DispatchPMCompleteAdjustmentBus) {
        let busObj: DispatchPMCompleteAdjustmentBus = new DispatchPMCompleteAdjustmentBus(dataObj);
        busObj.setBeforeSave();
        return busObj;
    }
    protected save(dataObj: DispatchPMCompleteAdjustmentBus) {
        if (this.multiAddMode) {
            this.multiSave(dataObj);
        } else {
            let busObj: DispatchPMCompleteAdjustmentBus = this.getChangedObject(dataObj);
            var isAdded = busObj.isAdded;
            this.svc_data.saveDispatchPMCompleteAdjustment(busObj).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    if (!this.addModeOnly) {
                        this.removeLocalAdded();
                    }
                    let newDataObj: DispatchPMCompleteAdjustmentBus = new DispatchPMCompleteAdjustmentBus(info.primaryReturn);
                    //if (isAdded)
                    //{
                    //    this.gridData.splice(0, 0, newDataObj);
                    //    this.cd.detectChanges();
                    //}
                    //newDataObj.visibleToCustomer = data.visibleToCustomer;

                    this.saveSuccess(newDataObj);
                    if (!this.addModeOnly) {
                        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPCOSTADJ");
                    }
                }
                else {
                    this.saveError(dataObj, "Error Saving");
                }
            });
        }
    }
    public multiSave(dataObj: DispatchPMCompleteAdjustmentBus) {
        this.jobIds.forEach((jobId, index) => {
            dataObj.dispatchId = jobId;
            //dataObj.isSixZeroSix = this.editForm.get([`subCheck-${index}`]).value;
            let busObj: DispatchPMCompleteAdjustmentBus = this.getChangedObject(dataObj);
            var isAdded = busObj.isAdded;
            this.svc_data.saveDispatchPMCompleteAdjustment(busObj).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    if (!this.addModeOnly) {
                        this.removeLocalAdded();
                    }
                    let newDataObj: DispatchPMCompleteAdjustmentBus = new DispatchPMCompleteAdjustmentBus(info.primaryReturn);
                    this.saveSuccess(newDataObj);
                    if (!this.addModeOnly) {
                        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPCOSTADJ");
                    }
                }
                else {
                    this.saveError(dataObj, "Error Saving");
                }
            });
        });
    }
    deleteIndividual(data: DispatchPMCompleteAdjustmentBus) {
        if (data.hasId) {
            //this.svc_data.deleteNote(data.dispatchNotesId).subscribe((dataObj: BaseMessageInfo) => {
            //    if (!dataObj.saveError) {
            //        let delDataObj: v_DispatchNoteBus = new v_DispatchNoteBus(data);

            //        this.deleteSuccess(delDataObj);
            //        this.removeLocal(delDataObj);
            //        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPATCHNOTES");
            //    }
            //    else {
            //        this.deleteError(data, dataObj.message);

            //    }
            //});
        }
        else {
            this.closeEditor();
        }
    }
    get canEditNote(): boolean {
        let can: boolean = true;

        //if (this.canEdit && this.selectedData) {
        //}

        return can;
    }
    processGridData() {
        this.costCodeCF = new MSCLFilterClass('dispatchCostAdjustmentCode', 'dispatchCostAdjustmentCode', this.gridData);
        if (CommonFunctions.hasArrayElementValue(this.gridData)) {
            this.aggregateResults = aggregateBy(this.gridData, this.aggregates);
        }
    }

}

